import AsyncStorage from '@react-native-async-storage/async-storage';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { BetValidationData } from '@/feature/betslip-pickem/api/types';
import { BetslipPick, PickAndEventWithStatus, PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { getUpdatedPicks } from '@/feature/betslip-pickem/utils/betslip-projection-changed';
import { useWalletStore } from '@/hooks/use-wallet';
import { Currency, EntryRulesOutput, GameMode } from '@/types/api.generated';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { removeEdgeComboForPlayerIds } from '../utils/betslip-edge-combos';
import { getDefaultEntryAmount, isFreeEntryPromo, mapValidationDataToGameModes } from '../utils/betslip-utils';
import { EntryAmountValidationData } from './use-betslip-actions';
import { FantasyEntryPromotionStatus } from './use-fantasy-entry-promotions';

const DEFAULT_VALIDATION_DATA = {
    DYNAMIC: {
        betValidation: undefined,
        amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
    },
    PERFECT: {
        betValidation: undefined,
        amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
    },
    MYSTERY: {
        betValidation: undefined,
        amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
    },
    RACE_TO_PLACE: {
        betValidation: undefined,
        amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
    },
} as const;

export type BetslipRefferer = {
    linkId?: string;
    entryId?: string;
    partnerName?: string;
    socialFeedEntryId?: string;
    raceToPlaceEntryId?: string;
};

export const betslipPlayerSelector =
    ({ eventId, playerId }: { eventId?: string; playerId: string }) =>
    (state: Betslip) => {
        return state.betslip.find(item => item.eventId === eventId && item.player.id === playerId);
    };

// 'auto' = we set the default, 'user' = the user typed or picked the amount
export type EntryAmountSource = 'auto' | 'user';

export interface Betslip {
    betslip: BetslipPick[];
    acceptedEdgeCombos: string[][];
    refferer?: BetslipRefferer;
    activeGameMode: GameMode;
    entryAmount: number | undefined;
    entryAmountSource: EntryAmountSource;
    selectedPromo: FantasyEntryPromotionStatus | undefined;
    previouslySelectedPromo: FantasyEntryPromotionStatus | undefined;
    autoPromoApplied: boolean;
    currency: Currency;
    validationData: Record<
        GameMode,
        { betValidation?: BetValidationData; amountValidation: EntryAmountValidationData }
    >;
    validating: boolean;
    isSyncingPicks: boolean;
    quickAmountsMode: 'add' | 'set';
    hasClaimedTopUpBonus: boolean;
    actions: {
        setActiveGameMode: (gameMode: GameMode) => void;
        setEntryAmount: (value: number | undefined) => void;
        setValidationData: (
            data: BetValidationData[],
            allEntryRules: EntryRulesOutput[],
            limitsValidation: Record<GameMode, EntryAmountValidationData>
        ) => void;
        setSelectedPromo: (promotion: FantasyEntryPromotionStatus | undefined) => void;
        setPreviouslySelectedPromo: (promotion: FantasyEntryPromotionStatus | undefined) => void;
        setAutoPromoApplied: (value: boolean) => void;
        resetAutoPromoApplied: () => void;
        setValidating: (value: boolean) => void;
        setIsSyncingPicks: (value: boolean) => void;
        setCurrency: (value: Currency, gameMode: GameMode) => void;
        selectPick: (pick: BetslipPick) => BetslipPick[];
        removePicks: (picks: { eventId: string; player: PlayerWithTeam }[]) => BetslipPick[];
        updateBetslipProjections: (picks: PickAndEventWithStatus[]) => void;
        updateBetslipProjection: (playerId: string, projection: PlayerProjectionFragment) => void;
        acceptEdgeCombo: (playerIds: string[]) => void;
        clearBetslip: () => void;
        setRefferer: (refferer: BetslipRefferer) => void;
        clearRefferer: () => void;
        replacePicks: (picks: BetslipPick[], refferer?: BetslipRefferer) => void;
        checkForBalanceError: () => boolean;
        setQuickAmountsMode: (mode: 'add' | 'set') => void;
        initAndGetEntryAmount: () => number | undefined;
        setHasClaimedTopUpBonus: (value: boolean) => void;
    };
}

/**
 * Computes the default entry amount based on currency, wallet balance, and max allowed amount.
 */
const computeDefaultEntryAmount = (state: Betslip, validationData: Betslip['validationData']): number => {
    const gameMode = state.selectedPromo?.promotion.type === 'MYSTERY' ? GameMode.Mystery : state.activeGameMode;
    const maxEntryAmount = validationData[gameMode].amountValidation.maxAllowedEntryAmount;
    const { betrBucks, realMoneyTotal } = useWalletStore.getState();
    return getDefaultEntryAmount(state.currency, { betrBucks, realMoneyTotal }, maxEntryAmount);
};

export const useBetslipStore = create<Betslip>()(
    persist(
        (set, get) => ({
            betslip: [],
            acceptedEdgeCombos: [],
            validationData: DEFAULT_VALIDATION_DATA,
            activeGameMode: GameMode.Perfect,
            entryAmount: undefined,
            entryAmountSource: 'auto',
            currency: Currency.Usd,
            selectedPromo: undefined,
            previouslySelectedPromo: undefined,
            autoPromoApplied: false,
            validating: false,
            isSyncingPicks: false,
            quickAmountsMode: 'set',
            hasClaimedTopUpBonus: false,
            actions: {
                initAndGetEntryAmount: () => {
                    const { entryAmount } = get();
                    const defaultEntryAmount = computeDefaultEntryAmount(get(), get().validationData);

                    const shouldInitEntryAmount = entryAmount === undefined && defaultEntryAmount > 0;

                    if (shouldInitEntryAmount) {
                        set({ entryAmount: defaultEntryAmount, entryAmountSource: 'auto' });
                        return defaultEntryAmount;
                    } else {
                        return entryAmount;
                    }
                },
                setActiveGameMode: gameMode => {
                    set({
                        activeGameMode: gameMode,
                    });
                },
                setEntryAmount: value => {
                    // undefined means "no amount yet", so the default can be set again
                    set({ entryAmount: value, entryAmountSource: value === undefined ? 'auto' : 'user' });
                },
                setValidationData: (allValidationData, allEntryRules, limitsValidation) => {
                    const newValidationData = mapValidationDataToGameModes(
                        allValidationData,
                        allEntryRules,
                        limitsValidation
                    );

                    const { entryAmount: currentEntryAmount, entryAmountSource } = get();
                    const validationChanged = !isEqual(newValidationData, get().validationData);

                    // Uses new validationData directly since state hasn't been updated yet.
                    const defaultEntryAmount = computeDefaultEntryAmount(get(), newValidationData);

                    // set the default if there is no amount yet, or lower our amount when the default drops
                    // (e.g. a free pick lowers the max). Never touch what the user typed, so they can see the error
                    const shouldApplyDefaultEntryAmount =
                        entryAmountSource === 'auto' &&
                        defaultEntryAmount > 0 &&
                        (currentEntryAmount === undefined || currentEntryAmount > defaultEntryAmount);

                    set({
                        validating: false,
                        ...(validationChanged ? { validationData: newValidationData } : {}),
                        ...(shouldApplyDefaultEntryAmount ? { entryAmount: defaultEntryAmount } : {}),
                    });
                },
                setSelectedPromo: value => {
                    set({
                        selectedPromo: value,
                    });
                },
                setPreviouslySelectedPromo: value => {
                    set({
                        previouslySelectedPromo: value,
                    });
                },
                setAutoPromoApplied: value => {
                    set({ autoPromoApplied: value });
                },
                resetAutoPromoApplied: () => {
                    set({ autoPromoApplied: false });
                },
                setValidating: value => {
                    set({
                        validating: value,
                    });
                },
                setIsSyncingPicks: value => {
                    set({
                        isSyncingPicks: value,
                    });
                },
                setCurrency: value => {
                    set({ currency: value });
                },
                selectPick: item => {
                    const items = get().betslip;
                    const existingItem = items.find(
                        it => it.player.id === item.player.id && it.eventId === item.eventId
                    );
                    let acceptedEdgeCombos = get().acceptedEdgeCombos;
                    let newItems;
                    if (existingItem) {
                        //We are replacing a projection, so the accepted edge combo for this player should be removed
                        acceptedEdgeCombos = removeEdgeComboForPlayerIds([existingItem.player.id], acceptedEdgeCombos);
                        newItems = items.map(it =>
                            it.player.id === item.player.id && it.eventId === item.eventId ? item : it
                        );
                    } else {
                        newItems = [...items, item];
                    }

                    set({ betslip: newItems, acceptedEdgeCombos: [...acceptedEdgeCombos], refferer: undefined });
                    return newItems;
                },
                setRefferer: refferer => {
                    set({ refferer });
                },
                replacePicks: (picks, refferer) => {
                    set({ betslip: picks, acceptedEdgeCombos: [], refferer });
                },
                removePicks: picks => {
                    let filteredItems = [...get().betslip];
                    if (picks.length > 0) {
                        picks.forEach(toRemove => {
                            filteredItems = filteredItems.filter(
                                it => !(it.player.id === toRemove.player.id && it.eventId === toRemove.eventId)
                            );
                        });
                        if (filteredItems.length === 0) {
                            //clear the entire betslip
                            get().actions.clearBetslip();
                            return [];
                        } else {
                            const edgeComboPairs = get().acceptedEdgeCombos;
                            //remove accepted edge combos for removed players
                            const filteredEdgeComboParis = removeEdgeComboForPlayerIds(
                                picks.map(it => it.player.id),
                                edgeComboPairs
                            );
                            set({
                                betslip: filteredItems,
                                acceptedEdgeCombos: filteredEdgeComboParis,
                                refferer: undefined,
                            });
                            return filteredItems;
                        }
                    }
                    return filteredItems;
                },
                updateBetslipProjections: (picks: PickAndEventWithStatus[]) => {
                    if (picks.length === 0) {
                        return;
                    }
                    set(state => {
                        const items = getUpdatedPicks({ betSlipPicks: state.betslip, changedPicks: picks });
                        return { betslip: items };
                    });
                },
                updateBetslipProjection: (playerId: string, projection: PlayerProjectionFragment) => {
                    set(state => {
                        const items = state.betslip.map(it => {
                            if (it.player.id === playerId) {
                                return { ...it, projection };
                            }
                            return it;
                        });
                        return { betslip: items };
                    });
                },
                acceptEdgeCombo: (playerIds: string[]) => {
                    set(state => {
                        const edgeCombos = state.acceptedEdgeCombos;
                        return { acceptedEdgeCombos: [...edgeCombos, playerIds] };
                    });
                },
                clearBetslip: () => {
                    //When clearing the betslip, keep the current active game mode for the next entry
                    const nextActiveGameMode = get().activeGameMode;

                    set({
                        betslip: [],
                        acceptedEdgeCombos: [],
                        refferer: undefined,
                        entryAmount: undefined,
                        entryAmountSource: 'auto',
                        activeGameMode: nextActiveGameMode,
                        currency: Currency.Usd,
                        selectedPromo: undefined,
                        previouslySelectedPromo: undefined,
                        autoPromoApplied: false,
                        quickAmountsMode: 'set',
                        validationData: {
                            DYNAMIC: {
                                betValidation: undefined,
                                amountValidation: {
                                    errorMessage: undefined,
                                    validEntry: false,
                                    maxAllowedEntryAmount: 0,
                                },
                            },
                            PERFECT: {
                                betValidation: undefined,
                                amountValidation: {
                                    errorMessage: undefined,
                                    validEntry: false,
                                    maxAllowedEntryAmount: 0,
                                },
                            },
                            MYSTERY: {
                                betValidation: undefined,
                                amountValidation: {
                                    errorMessage: undefined,
                                    validEntry: false,
                                    maxAllowedEntryAmount: 0,
                                },
                            },
                            RACE_TO_PLACE: {
                                betValidation: undefined,
                                amountValidation: {
                                    errorMessage: undefined,
                                    validEntry: false,
                                    maxAllowedEntryAmount: 0,
                                },
                            },
                        },
                    });
                },
                clearRefferer: () => {
                    set({ refferer: undefined });
                },
                checkForBalanceError: () => {
                    const entryAmount = get().entryAmount ?? 0;
                    const currency = get().currency;
                    if (entryAmount === 0) {
                        return false;
                    }

                    if (isFreeEntryPromo(get().selectedPromo)) {
                        // free entries do not need a balance, so skip the balance error
                        return false;
                    }

                    const availableBalance =
                        currency === Currency.Fre
                            ? useWalletStore.getState().betrBucks || 0
                            : useWalletStore.getState().realMoneyTotal || 0;

                    // A balance error should only be reported when depositing would actually unblock the
                    // entry. maxAllowedEntryAmount is the rule/wager-limit cap (it excludes the wallet
                    // balance), so if the entry exceeds it, no deposit helps and it's a validation error,
                    // not a balance error.
                    const activeGameMode = get().activeGameMode;
                    const maxAllowedEntryAmount =
                        get().validationData[activeGameMode].amountValidation.maxAllowedEntryAmount;
                    const withinAllowedMax = maxAllowedEntryAmount > 0 && entryAmount <= maxAllowedEntryAmount;

                    return withinAllowedMax && entryAmount > availableBalance;
                },
                setQuickAmountsMode: mode => {
                    set({ quickAmountsMode: mode });
                },
                setHasClaimedTopUpBonus: value => {
                    set({ hasClaimedTopUpBonus: value });
                },
            },
        }),
        {
            name: 'betslip-storage',
            version: 38,
            storage: createJSONStorage(() => AsyncStorage),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, validating, hasClaimedTopUpBonus, ...rest }: Betslip) => rest,
        }
    )
);
